import { useState } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import { Button } from "waapi/waap-ui/components/Button/Button.js";
import { Heading } from "waapi/waap-ui/components/Heading/Heading.js";
import { HeadingSize, HeadingWeight } from "waapi/waap-ui/components/Heading/Heading.types.js";
import { Text } from "waapi/waap-ui/components/Text/Text.js";
import { TextSize, TextWeight } from "waapi/waap-ui/components/Text/Text.types.js";
import { ContactAdvantagesEnum } from "waapi/palpatine-client/helpers/types/ContractAdvantage.types.js";
import { useAccountManager } from "../../../entities/Account/hooks/useAccountManager/index.js";
import { useTranslation } from "../../../customHooks/useTranslation/index.js";
import { actions } from "../../../entities/User/hooks/useUserState/slice.js";
import {
    AdvantageCardInner,
    AdvantageCardWrapper,
    CardBody,
    CardHeader,
    CardTitleGroup,
    CardsRow,
    Header,
    MostPopularBadge,
    OuterCard,
    PageWrapper,
    RadioCircle,
    TiersDescription,
} from "./style.js";
import { Alert } from "waapi/waap-ui/components/Alert/Alert.js";
import { CrownBold } from "waapi/waap-ui/components/Iconsax/CrownBold.js";
import { ICONSAX_SIZE } from "waapi/waap-ui/components/Iconsax/Iconsax.types.js";

export const ChooseAdvantage = (): JSX.Element => {
    const [loading, setLoading] = useState(false);
    const [advantage, setAdvantage] = useState<ContactAdvantagesEnum>(ContactAdvantagesEnum.ADVANTAGE_30_20);

    const { chooseAdvantage } = useAccountManager();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t, translations } = useTranslation();

    const handleChooseAdvantage = async () => {
        setLoading(true);
        await chooseAdvantage({
            params: [{ body: { contract: advantage } }],
            onAdvantageChosen: () => dispatch(actions.setAdvantage(advantage)),
        });
        setLoading(false);
    };

    return (
        <PageWrapper>
            <OuterCard>
                <Header>
                    <Heading size={HeadingSize.MEDIUM} weight={HeadingWeight.SEMIBOLD} color={theme.grayscale[100]}>
                        {t(translations.pages.choose_advantage.title())}
                    </Heading>
                    <Alert>
                        <Text size={TextSize.MEDIUM}>{t(translations.pages.choose_advantage.warning())}</Text>
                    </Alert>
                </Header>

                <CardsRow>
                    <AdvantageCardWrapper
                        $selected={advantage === ContactAdvantagesEnum.ADVANTAGE_50}
                        onClick={() => setAdvantage(ContactAdvantagesEnum.ADVANTAGE_50)}
                    >
                        <AdvantageCardInner>
                            <CardHeader>
                                <CardTitleGroup>
                                    <Text size={TextSize.MEDIUM} weight={TextWeight.BOLD} color={theme.grayscale[100]}>
                                        {t(translations.pages.choose_advantage.revenue_plan.title())}
                                    </Text>
                                    <Text size={TextSize["XX-SMALL"]} color={theme.grayscale[80]}>
                                        {t(translations.pages.choose_advantage.revenue_plan.subtitle())}
                                    </Text>
                                </CardTitleGroup>
                                <RadioCircle $selected={advantage === ContactAdvantagesEnum.ADVANTAGE_50} />
                            </CardHeader>
                            <CardBody>
                                <Text size={TextSize.LARGE} weight={TextWeight.BOLD} color={theme.grayscale[100]}>
                                    {t(translations.pages.choose_advantage.revenue_plan.commission())}
                                </Text>
                                <Text size={TextSize.MEDIUM} color={theme.grayscale[80]}>
                                    {t(translations.pages.choose_advantage.revenue_plan.benefit())}
                                </Text>
                            </CardBody>
                            <TiersDescription>{t(translations.pages.choose_advantage.revenue_plan.tiers())}</TiersDescription>
                        </AdvantageCardInner>
                    </AdvantageCardWrapper>

                    <AdvantageCardWrapper
                        $selected={advantage === ContactAdvantagesEnum.ADVANTAGE_30_20}
                        onClick={() => setAdvantage(ContactAdvantagesEnum.ADVANTAGE_30_20)}
                    >
                        <MostPopularBadge>
                            {t(translations.pages.choose_advantage.conversion_plan.badge())}
                            <CrownBold size={ICONSAX_SIZE.SMALL} color="white" />
                        </MostPopularBadge>
                        <AdvantageCardInner>
                            <CardHeader>
                                <CardTitleGroup>
                                    <Text size={TextSize.MEDIUM} weight={TextWeight.BOLD} color={theme.grayscale[100]}>
                                        {t(translations.pages.choose_advantage.conversion_plan.title())}
                                    </Text>
                                    <Text size={TextSize["XX-SMALL"]} color={theme.grayscale[80]}>
                                        {t(translations.pages.choose_advantage.conversion_plan.subtitle())}
                                    </Text>
                                </CardTitleGroup>
                                <RadioCircle $selected={advantage === ContactAdvantagesEnum.ADVANTAGE_30_20} />
                            </CardHeader>
                            <CardBody>
                                <Text size={TextSize.LARGE} weight={TextWeight.BOLD} color={theme.grayscale[100]}>
                                    {t(translations.pages.choose_advantage.conversion_plan.commission())}
                                </Text>
                                <Text size={TextSize.MEDIUM} color={theme.grayscale[80]}>
                                    {t(translations.pages.choose_advantage.conversion_plan.benefit())}
                                </Text>
                            </CardBody>
                            <TiersDescription>{t(translations.pages.choose_advantage.conversion_plan.tiers())}</TiersDescription>
                        </AdvantageCardInner>
                    </AdvantageCardWrapper>
                </CardsRow>

                <Button color="primary" loading={loading} onClick={handleChooseAdvantage}>
                    {t(translations.pages.choose_advantage.validate())}
                </Button>
            </OuterCard>
        </PageWrapper>
    );
};
